import type { TestId } from '@trello/test-ids';
import classNames from 'classnames';
import { forwardRefComponent } from 'app/src/forwardRefComponent';
import { RouterLink } from 'app/src/components/RouterLink';
import type { FunctionComponent } from 'react';
import React from 'react';
import styles from './PopoverMenu.less';
import { useFocusRing } from '@trello/a11y';

interface ContentProps {
  description?: React.ReactNode;
  rawDescription?: string;
  title?: React.ReactNode;
  icon?: JSX.Element;
}

interface PopoverMenuButtonProps extends ContentProps {
  onClick: React.EventHandler<React.MouseEvent<HTMLButtonElement>>;
  className?: string;
  disabled?: boolean;
  testId?: TestId;
}

interface PopoverMenuLinkProps extends ContentProps {
  href: string;
  target?: string;
  onClick?: React.EventHandler<React.MouseEvent<HTMLAnchorElement>>;
  testId?: TestId;
  className?: string;
  icon?: JSX.Element;
  external?: boolean;
}

export const PopoverMenuItemSeparator: FunctionComponent<{
  fullWidth?: boolean;
}> = ({ fullWidth }) => (
  <li
    className={classNames({
      [styles.separator]: true,
      [styles.fullWidth]: fullWidth,
    })}
  ></li>
);

const PopoverMenuItemContents: FunctionComponent<ContentProps> = ({
  children,
  description,
  rawDescription,
  title,
  icon,
}) => (
  <>
    {icon && React.cloneElement(icon, { size: 'small' })}
    <span
      className={classNames(styles.title, description && styles.withoutMargins)}
    >
      {title ? title : children}
    </span>
    {(description || rawDescription) && (
      <p className={styles.description}>
        {description ? description : rawDescription}
      </p>
    )}
  </>
);

const PopoverMenuItem: FunctionComponent = ({ children }) => (
  <li className={classNames(styles.item)}>{children}</li>
);

export const PopoverMenuLink: FunctionComponent<PopoverMenuLinkProps> = ({
  testId,
  href,
  target,
  className,
  onClick,
  external = false,
  ...contentProps
}) => {
  const [hasFocusRing, mouseEvents] = useFocusRing();
  const LinkComponent = external ? 'a' : RouterLink;
  const linkComponentProps = {
    href: href,
    onClick: onClick,
    target: target,
    ...(LinkComponent === RouterLink
      ? { testId: testId }
      : { 'data-testid': testId }),
  };

  return (
    <PopoverMenuItem>
      <LinkComponent
        className={classNames(
          styles.link,
          className,
          hasFocusRing && styles.linkFocusRing,
        )}
        {...linkComponentProps}
        {...mouseEvents}
      >
        <PopoverMenuItemContents {...contentProps} />
      </LinkComponent>
    </PopoverMenuItem>
  );
};

export const PopoverMenuButton = forwardRefComponent<
  HTMLButtonElement,
  PopoverMenuButtonProps
>(
  'PopoverMenuButton',
  // @ts-expect-error
  (
    {
      onClick,
      className,
      disabled,
      testId,
      ...contentProps
    }: PopoverMenuButtonProps,
    ref: React.RefObject<HTMLButtonElement>,
  ) => {
    const [hasFocusRing, mouseEvents] = useFocusRing();

    return (
      <PopoverMenuItem>
        <button
          className={classNames(
            styles.link,
            hasFocusRing && styles.linkFocusRing,
            className,
          )}
          disabled={disabled}
          onClick={onClick}
          data-testid={testId}
          ref={ref}
          {...mouseEvents}
        >
          <PopoverMenuItemContents {...contentProps} />
        </button>
      </PopoverMenuItem>
    );
  },
);

interface PopoverMenuProps {
  className?: string;
  testId?: TestId;
}

export const PopoverMenu: FunctionComponent<PopoverMenuProps> = ({
  className,
  children,
  testId,
}) => (
  <nav className={className}>
    <ul data-testid={testId}>{children}</ul>
  </nav>
);
